import { useEffect, useState } from 'react';
import { Button, Group, Modal, Stack, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { APP_VERSION } from '../shared/version';
import {
  applyPendingUpdate,
  checkForUpdate,
  getRequiredUpdate,
  subscribeToUpdate,
} from '../shared/versionCheck';

/**
 * "A new version is available" — as a required acknowledgement, not an offer.
 *
 * No close button, no Escape, no click-outside: one OK button that applies the update. A prompt
 * that could be dismissed meant a copy could stay behind indefinitely, keeping the route table it
 * was built with, and a deploy that adds a route then leaves those users looking at "Not Found" on
 * a link that works for everybody else. GOS and LOS both do it this way.
 *
 * Mounted above the router in App, so it reaches every screen — including Sign In, which
 * previously had no route to an update at all: you had to log in first and find the profile menu.
 *
 * WHY IT STILL ASKS RATHER THAN RELOADING ITSELF
 * This runs in warehouses. Reloading unannounced mid-count loses somebody's work, so the choice
 * removed is whether to update, not when to look up from what you were doing.
 *
 * THE ONE WAY OUT
 * If the update cannot be installed — a service worker missing from the deployment, say — applying
 * it clears the pending state and reports why, and this closes. An OK button that cannot work must
 * never be the only exit, or a bad deploy locks everybody out of the entire app.
 */
export function UpdateRequiredModal() {
  const { t } = useTranslation();
  // A plain subscription rather than useSyncExternalStore. Not because that hook was proven wrong —
  // an earlier note here claimed it was, on the strength of a browser check that turned out to be
  // measuring nothing (the tool it ran in cannot open ANY Mantine modal, including ones this change
  // never touched). It is just the boring version: two hooks, no snapshot-identity rules to get
  // subtly wrong. Seeded from the store rather than null, and re-read on subscribe, because the
  // update can be detected before this mounts — the poll starts in main.tsx.
  const [deployed, setDeployed] = useState<string | null>(getRequiredUpdate);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    setDeployed(getRequiredUpdate());
    // Ask on mount as well as listening. The poll only fires on an interval and on tab focus, so
    // without this a copy that is already behind shows nothing until one of those comes round.
    void checkForUpdate(false);
    return subscribeToUpdate((version) => setDeployed(version));
  }, []);

  async function onAcknowledge() {
    setApplying(true);
    try {
      // Resolving without navigating away means it could not be applied; the state is cleared and
      // a notification explains. Reset so the button is usable if this somehow renders again.
      await applyPendingUpdate();
    } finally {
      setApplying(false);
    }
  }

  return (
    <Modal
      opened={deployed !== null}
      onClose={() => {
        /* Deliberately inert. The only way past this is the button. */
      }}
      withCloseButton={false}
      closeOnEscape={false}
      closeOnClickOutside={false}
      centered
      title={t('shell.updateRequiredTitle')}
      // Above the notification layer, so the blocked-update message cannot land behind it.
      zIndex={1000}
    >
      <Stack gap="md">
        <Text size="sm">
          {t('shell.updateRequiredBody', { deployed: deployed ?? '', current: APP_VERSION })}
        </Text>
        <Text size="xs" c="dimmed">
          {t('shell.updateRequiredHint')}
        </Text>
        <Group justify="flex-end">
          <Button color="invetrak" onClick={() => void onAcknowledge()} loading={applying}>
            {t('shell.updateRequiredConfirm')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
